/**
 * AIチューター「パルの ひとこと」(docs/07_learning_support.md §7)。
 *
 * docs/07 §7:
 *   「callable `askTutor`(Functions・Auth+App Check必須): 入力 `{kind, context?}` → 出力
 *     `{message, mood, tips[], isAi}`」
 *   「クライアントからの補助文脈は**数値・enumのみ**」
 *
 * クライアントがやることは3つだけ:
 *   1. どの場面か(`kind`)を伝える
 *   2. **数値と enum だけ**の補助文脈を添える(自由文字列は型として持たない)
 *   3. 返ってきたことばを吹き出しに出す
 *
 * 何を知っているか(実績・苦手・コイン)は**サーバーが組み立てる**。
 * ローカルモード(Firebase 未設定)では `NoTutorService` が `supported: false` を
 * 返し、画面は「ログインすると パルが おはなしできるよ」と案内する。
 *
 * ここは `factory.ts` から静的に読まれるので、重いものを import しないこと。
 * (`TutorSketch` は**型だけ**の import なので、ビルド後には1バイトも残らない)
 */
import type { TutorSketch } from '@paltyping/quiz';

/** どの場面のひとことか(`hint` = まなびの詰まりヒント。docs/07 §7.4)。 */
export type TutorKind = 'daily' | 'result' | 'consult' | 'hint';

/** どのモジュールのリザルトか。 */
export type TutorModule = 'typing' | 'learn' | 'puzzle';

/** 相談ボタン(定型の3つ。自由入力は出さない = 安全・低コスト)。 */
export type TutorQuestion = 'today' | 'weakness' | 'coins';

/** パルの表情(`@paltyping/quiz` の `LessonMood` と同じ語彙)。 */
export type TutorMood = 'normal' | 'happy' | 'puzzled';

/**
 * 補助文脈。**数値と enum しか無い**(型の時点で文字列を持たせない)。
 * サーバー側も同じ表で弾くので、ここを増やすときは両方を直すこと。
 */
export interface TutorContext {
  readonly module?: TutorModule;
  readonly question?: TutorQuestion;
  /** そのプレイでついた星(0〜5)。 */
  readonly stars?: number;
  readonly correct?: number;
  readonly total?: number;
  /** ミス率(0〜1)。 */
  readonly missRate?: number;
  /** 打鍵数/分。 */
  readonly kpm?: number;
  /** 端末に記録されている にがての数。 */
  readonly weakCount?: number;
  /** 連続で学んだ日数。 */
  readonly streakDays?: number;
}

/**
 * 詰まりヒントの入力(docs/07 §7.4「入力は band/questionId/level/誤答回数など**IDと数値のみ**」)。
 *
 * 問題文も選択肢も**送らない**。サーバーが同梱バンクから引くので、
 * ここから出ていくのは ID と数値だけ(= 送信内容から答えが漏れようがない)。
 */
export interface TutorHintInput {
  readonly band: string;
  readonly questionId: string;
  /** 1 = 着眼点 / 2 = 考え方。 */
  readonly level: 1 | 2;
  /** 続けてまちがえた回数。 */
  readonly wrongCount: number;
}

export interface TutorReply {
  readonly message: string;
  readonly mood: TutorMood;
  readonly tips: readonly string[];
  /** `false` = サーバーの定型文(キー未設定・上限・失敗)。画面は区別しない。 */
  readonly isAi: boolean;
  /**
   * **絵のヒント**(docs/07 §7.4 のスケッチ。任意)。
   *
   * 付くのは `hint` で、サーバーの形の検証と答え漏れフィルタを通ったときだけ。
   * 付いていなければ画面は**手元の辞書スケッチ**を出したままにする
   * (= 通信が失敗しても絵は消えない)。
   */
  readonly sketch?: TutorSketch;
}

/**
 * パルのひとことの窓口。
 *
 * 取れなければ `null`。**画面はそれで止まらない**
 * (リザルトの吹き出しは何も出さない / パル画面は定型の案内を出す)。
 */
export interface TutorService {
  readonly supported: boolean;
  ask(kind: TutorKind, context?: TutorContext): Promise<TutorReply | null>;
  /**
   * まなびの詰まりヒント(docs/07 §7.4)。
   *
   * **画面はこれを待たない**。辞書ヒント(`@paltyping/quiz` の skills 表)を
   * 先に出しておいて、返ってきたら差し替える。取れなければ `null` =
   * 辞書ヒントのまま(子どもには何も起きていないように見える)。
   */
  hint(input: TutorHintInput): Promise<TutorReply | null>;
}

/**
 * ローカルモード用の何もしない実装。
 *
 * サーバーが文脈を組み立てる前提の機能なので、ローカルでは成立しない。
 * 「あそべません」ではなく「ログインすると おはなしできるよ」と案内する
 * (期待させないが、道は見せる)。
 */
export class NoTutorService implements TutorService {
  readonly supported = false;

  async ask(): Promise<TutorReply | null> {
    return null;
  }

  /** ヒントは辞書だけで完全に成立する(docs/07 §7.4)ので、黙って `null`。 */
  async hint(): Promise<TutorReply | null> {
    return null;
  }
}

// ── 画面のことば(ひらがな主体。CLAUDE.md ルール5)────────────────────

/** 相談ボタンの見た目(送るのは ID だけ)。 */
export const TUTOR_QUESTIONS: readonly {
  readonly id: TutorQuestion;
  readonly emoji: string;
  readonly label: string;
}[] = [
  { id: 'today', emoji: '🌱', label: 'きょうは なにを やれば いい?' },
  { id: 'weakness', emoji: '🔧', label: 'にがてを へらすには?' },
  { id: 'coins', emoji: '🪙', label: 'もっと コインを ためたい!' },
];

export const TUTOR_LABELS = {
  cardTitle: '💬 パルの ひとこと',
  loading: 'かんがえちゅう…',
  signedOut: 'ログインすると パルが おはなしできるよ',
  consultTitle: 'パルに きいてみる',
} as const;
